import { creating, exist } from '@campaign-portal/namespace/common/id';
import {
	Campaign,
	CampaignInfo,
	CampaignRepeatType,
	CampaignSettings,
	CampaignStatistics,
	CampaignStatus,
} from '@campaign-portal/namespace/entities/campaigns/specs';
import { AuditableObject } from '@campaign-portal/namespace/common/auditableObject';
import { CampaignRecurrenceType, ChannelType, PreModerationDeclineType } from '@campaign-portal/namespace/common/enums';

export interface CampaignEntityConfig<ID = exist | creating> extends Partial<Campaign<ID>> {
	id: ID;
	info: CampaignInfo<ID>;
	setup: Campaign<ID>['setup'];
	settings: CampaignSettings;
}

export class CampaignEntity<ID = exist | creating> implements Campaign<ID> {
	[key: string]: unknown;

	readonly id: ID;
	readonly info: CampaignInfo<ID>;
	readonly setup: Campaign<ID>['setup'];
	readonly settings: CampaignSettings;
	readonly audit?: AuditableObject;
	readonly statistics?: CampaignStatistics;
	readonly partnerId: Campaign['partnerId'];
	readonly declineType?: PreModerationDeclineType;
	readonly declineReason?: string;
	readonly recurrentHistory?: CampaignEntity<exist>[];

	constructor(config: CampaignEntityConfig<ID>) {
		this.id = config.id;
		this.info = config.info;
		this.setup = config.setup;
		this.settings = config.settings;
		this.audit = config.audit;
		this.statistics = config.statistics;
		this.partnerId = config.partnerId;
		this.declineType = config.declineType;
		this.declineReason = config.declineReason;
		this.recurrentHistory = config.recurrentHistory?.map((campaign) => new CampaignEntity(campaign));
	}

	isScheduled(): boolean {
		return this.info.status === CampaignStatus.SCHEDULED;
	}

	isPaused(): boolean {
		return this.info.status === CampaignStatus.PAUSED;
	}

	isInProgress(): boolean {
		return this.info.status === CampaignStatus.IN_PROGRESS;
	}

	isCompleted(): boolean {
		return this.info.status === CampaignStatus.COMPLETED;
	}

	isFailed(): boolean {
		return this.info.status === CampaignStatus.FAILED;
	}

	isCancelled(): boolean {
		return this.info.status === CampaignStatus.CANCELLED;
	}

	isWaitingForApproval(): boolean {
		return this.info.status === CampaignStatus.WAITING_FOR_APPROVAL;
	}

	isTerminal(): boolean {
		return this.isCompleted() || this.isFailed() || this.isCancelled();
	}

	canStart(): boolean {
		return this.isScheduled();
	}

	canPause(): boolean {
		return this.isInProgress();
	}

	canResume(): boolean {
		return this.isPaused();
	}

	canCancel(): boolean {
		return !this.isInProgress() && !this.isTerminal();
	}

	canRepeat(): boolean {
		if (this.isFromFile()) {
			return false;
		}
		return this.isTerminal();
	}

	canDelete(): boolean {
		if (
			this.settings.repeatRule?.repeatType === CampaignRepeatType.CONTACT_DATE &&
			this.settings.recurrenceType === CampaignRecurrenceType.RECURRENT
		) {
			return false;
		}
		return !this.isInProgress();
	}

	isFromFile(): boolean {
		return Object.hasOwn(this.setup, 'file');
	}

	getChannelTypes(): ChannelType[] {
		return this.setup.channels.map((ch) => ch.channelType);
	}

	isDisabledRow(): boolean {
		return this.isCancelled();
	}
}
